import { SeriesGroup, SeriesItem, SeriesOptions } from './series'

/**
 * Calculate median of a dataset.
 */
export function median(group: SeriesGroup, options: SeriesOptions): SeriesItem[] {
  if (!options.key) {
    throw new Error("An option 'key' is required.")
  }
  if (group.members.length === 0) {
    return [{
      timestamp: (group.from + group.to) / 2,
      [options.key]: 0
    }]
  }
  const values = group.members.map(m => m[options.key || ''] as number)
  const v = values.sort((a, b) => a - b)
  const mid = Math.floor(v.length / 2)
  const median = (v.length % 2 !== 0) ? v[mid] : (v[mid - 1] + v[mid]) / 2
  return [{
    timestamp: (group.from + group.to) / 2,
    [options.key]: median
  }]
}

/**
 * Calculate mean of a dataset.
 */
export function mean(group: SeriesGroup, options: SeriesOptions): SeriesItem[] {
  if (group.members.length === 0) {
    return []
  }
  if (!options.key) {
    throw new Error("An option 'key' is required.")
  }
  const sum = group.members.map(m => m[options.key || ''] as number).reduce((a, b) => a + b, 0)
  return [{
    timestamp: (group.from + group.to) / 2,
    [options.key]: sum / group.members.length
  }]
}

/**
 * Simple Moving Average.
 */
export function sma(group: SeriesGroup, options: SeriesOptions): SeriesItem[] {
  const { key } = options
  const N = options.n || 15
  const K = options.k || 5

  if (group.members.length < N) {
    return []
  }

  if (!key) {
    throw new Error("An option 'key' is required.")
  }

  let sum = 0
  for (let i = 0; i < N; i++) {
    sum += group.members[i][key] as number
  }

  let item0 = group.members[0][key] as number

  const result: SeriesItem[] = []
  for (let j = N; j < group.members.length; j++) {
    if (j % K === 0) {
      result.push({
        timestamp: group.members[j].timestamp,
        [key]: sum / N
      })
    }
    sum = sum - item0 + (group.members[j][key] as number)
    item0 = group.members[j - N + 1][key] as number
  }

  return result
}

/**
 * Pick first item per group or `null` if no members in the group.
 */
export function first(group: SeriesGroup, options: SeriesOptions): SeriesItem[] {
  if (group.members.length === 0) {
    return []
  }
  const { key } = options
  if (!key) {
    throw new Error("An option 'key' is required.")
  }
  return [{
    timestamp: (group.from + group.to) / 2,
    [key]: group.members[0][key]
  }]
}

/**
 * Verify that sets are identical.
 */
export function setEquals<T>(x: Set<T>, y: Set<T>): boolean {
  return x.size === y.size && [...x].every(a => y.has(a))
}

/**
 * Largest value of a series, or `-Infinity` for an empty one.
 *
 * `Math.max(...values)` passes every value as an argument, which overflows the
 * call stack once a series grows past a few tens of thousands of points — the
 * limit is well below what a wide chart can hold and lower still on mobile.
 * Both helpers below coerce and compare exactly like `Math.max`/`Math.min` do
 * (`null` counts as 0, a single `NaN` poisons the result) so chart scaling is
 * unchanged, but they never grow the stack.
 */
export function maxOf(values: readonly unknown[]): number {
  let max = -Infinity
  for (const value of values) {
    const number = Number(value)
    if (Number.isNaN(number)) {
      return NaN
    }
    if (number > max) {
      max = number
    }
  }
  return max
}

/**
 * Smallest value of a series, or `Infinity` for an empty one. See `maxOf`.
 */
export function minOf(values: readonly unknown[]): number {
  let min = Infinity
  for (const value of values) {
    const number = Number(value)
    if (Number.isNaN(number)) {
      return NaN
    }
    if (number < min) {
      min = number
    }
  }
  return min
}
